import { useSettings } from "../../context/SettingsContext";
import { Toggle, SettingRow, SectionCard, ColorInput, NumberInput, TextInput, InfoBox } from "../ui/index";

export default function GeneralPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Variation Images"
                description="Control how product variation images interact with the gallery."
            >
                <SettingRow
                    label="Variation Image Change"
                    description="Automatically swap the main image and add a gallery entry when a product variation with its own image is selected."
                >
                    <Toggle
                        value={settings.variationImageChange}
                        onChange={(v) => s("variationImageChange", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Variation Gallery"
                    description="Allow each variation to have its own set of gallery thumbnail images. When a variation is selected the entire gallery is replaced with the variation's images. Requires the gallery field to be filled in on each product variation."
                >
                    <Toggle
                        value={settings.variationGallery}
                        onChange={(v) => s("variationGallery", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Performance"
                description="Control image loading behavior."
            >
                <SettingRow
                    label="Preload Images"
                    description="Preload all gallery and zoom images in the background on page load for a smoother experience."
                >
                    <Toggle
                        value={settings.preloadImages}
                        onChange={(v) => s("preloadImages", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Zoom Before Image Loads"
                    description="Allow zoom to activate immediately without waiting for the image to finish loading. Off by default — disabling the wait ensures zoom window sizing is always accurate."
                >
                    <Toggle
                        value={settings.zoomBeforeLoad}
                        onChange={(v) => s("zoomBeforeLoad", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Loading Spinner"
                description="Show an animated spinner while images are loading."
            >
                <SettingRow
                    label="Enable Spinner"
                    description="Display a loading indicator while images are being fetched."
                >
                    <Toggle
                        value={settings.spinner}
                        onChange={(v) => s("spinner", v)}
                    />
                </SettingRow>

                {settings.spinner && (
                    <SettingRow
                        label="Spinner Color"
                        description="The color of the loading spinner animation."
                    >
                        <ColorInput
                            value={settings.spinnerColor}
                            onChange={(v) => s("spinnerColor", v)}
                        />
                    </SettingRow>
                )}
            </SectionCard>
            <SectionCard
                title="Sticky Summary"
                description="Keep the product summary (title, price, add-to-cart) fixed in the viewport while scrolling. Designed for wide-screen layouts where the gallery and summary sit side by side."
            >
                <SettingRow
                    label="Enable"
                    description="Apply sticky positioning to the product summary alongside the gallery."
                >
                    <Toggle
                        value={
                            settings.stackedStickyEnabled !== false &&
                            !!settings.stackedStickyEnabled
                        }
                        onChange={(v) => s("stackedStickyEnabled", v)}
                    />
                </SettingRow>

                {settings.stackedStickyEnabled && (
                    <>
                        <SettingRow
                            label="Fix Theme Overflow"
                            description="Automatically override overflow:hidden on parent elements that would block the sticky effect. Disable if your theme layout breaks with this on."
                        >
                            <Toggle
                                value={settings.stackedStickyFixOverflow !== false}
                                onChange={(v) => s("stackedStickyFixOverflow", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Summary Selector"
                            description="CSS selector for the product summary element. Default targets the standard WooCommerce summary div."
                        >
                            <TextInput
                                value={settings.stackedStickySelector}
                                onChange={(v) => s("stackedStickySelector", v)}
                                placeholder=".summary.entry-summary"
                                width="w-52"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Top Offset"
                            description="Distance from the top of the viewport when the summary is in sticky position."
                        >
                            <NumberInput
                                value={settings.stackedStickyTop}
                                onChange={(v) => s("stackedStickyTop", v)}
                                min={0}
                                max={300}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Disable Below"
                            description="Viewport width below which sticky is disabled — keeps the effect large-screen only. Set to 0 to always enable."
                        >
                            <NumberInput
                                value={settings.stackedStickyBreakpoint}
                                onChange={(v) => s("stackedStickyBreakpoint", v)}
                                min={0}
                                max={2560}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <InfoBox className="mt-2 mb-2">
                            Sticky summary works when the gallery and summary are
                            in a <strong>side-by-side flex or grid layout</strong> —
                            the most common WooCommerce single-product template.
                            If your theme stacks them vertically, this option has
                            no visible effect.
                        </InfoBox>
                    </>
                )}
            </SectionCard>
        </div>
    );
}
